import { formatPercent } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { EchartsDirective, EchartsOption } from '@src/app/shared/directives/echarts.directive';
import { cn } from '@src/app/shared/utils/common.util';
import { formatNumberToKMB } from '@src/app/shared/utils/number.util';

import { Router } from '@angular/router';
import { MemeDashboardService } from '@src/app/modules/meme-dashboard/meme-dashboard.service';
import { MemeToken, TokenMarketCapType } from '@src/app/modules/meme-dashboard/meme-dashboard.type';
import { CommonService } from '@src/app/services/common.service';
import { omitFunc } from '@src/app/shared/pipes/omit.pipe';
import { getChainImg, getChainSymbolIconByChainName } from '@src/app/shared/types/wallet-chain.type';
import { convertTreemapData } from '@src/app/shared/utils/chart.util';
import dayjs from 'dayjs';
import { COMMON_TOOLTIP } from '../../../../token-detail/token-detail.data';

const largeColors = ['#FEE384', '#FFC934', '#FFA310', '#FF7010'];
const midColors = ['#CBF484', '#97ED61', '#56DE1C', '#46A400'];
const smallColors = ['#96EEDB', '#62E0F1', '#46C5EB', '#0082D8'];

@Component({
    selector: 'app-base-treemap',
    templateUrl: './base-treemap.component.html',
    styleUrls: ['./base-treemap.component.less'],
})
export class BaseTreemapComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() type: TokenMarketCapType;
    @Input() data: MemeToken[];

    // 具体展示的值采用哪个字段
    // 如果是 watchlist 则采用 watchlist_buy_amount_usd, watchlist_buy_percentage
    // 如果是smart_buy 则采用 smart_money_buy_amount_usd,smart_money_swap_percentage
    @Input() valueType: 'smart_buy' | 'watchlist' = 'smart_buy';

    // 是否展示Toggle Detail Treemap 按钮
    @Input() detailBtnVisible = true;

    @Input() shownDetailedTreemap: TokenMarketCapType;

    @Output() shownDetailedTreemapChange = new EventEmitter<void>();

    @ViewChild(EchartsDirective) echartsDirective: EchartsDirective;

    get valueKey(): 'smart_money_swap_amount_usd' | 'watchlist_buy_amount_usd' {
        return this.valueType === 'smart_buy' ? 'smart_money_swap_amount_usd' : 'watchlist_buy_amount_usd';
    }

    get percentKey(): 'smart_money_swap_percentage' | 'watchlist_buy_percentage' {
        return this.valueType === 'smart_buy' ? 'smart_money_swap_percentage' : 'watchlist_buy_percentage';
    }

    TREEMAP_CHART_TOOLTIP = () => ({
        ...COMMON_TOOLTIP,
        formatter: (params: any) => {
            const name = params.data.symbol || params.data.name;
            const image = params.data.image;
            const tokenAddress = params.data.token_address;
            const marketCap = params.data.market_cap;
            const liquidity = params.data.dex_liquidity;
            const smartMoneyBuys = params.data[this.valueKey];
            const percent = params.data[this.percentKey];

            const line1 = cn('flex-start-center gap-x-3');
            const chainIcon = cn('w-4 h-4 object-contain border border-black rounded-full absolute bottom-0 -right-2');
            const imgCls = cn('w-full h-full rounded-full object-contain');
            const chainImg = getChainSymbolIconByChainName(params.data.chain_name);

            return `<div>
                        <div class="${line1}">
                            <div style="position:relative;width:32px;height:32px;">
                                ${
                                    image
                                        ? `<img class="${imgCls}" src="${image}"/>`
                                        : `<div class="w-full h-full rounded-full bg-[#F7F7F7] flex-center-center text-black">${name?.slice(0, 2) || ''}</div>`
                                }
                                <img src="${chainImg}" class="${chainIcon}" />
                            </div>
                            <div>
                                <div style="font-weight: 500;">
                                    <span>${name}  </span>
                                    ${
                                        params.data.newMEME
                                            ? '<img src="/assets/imgs/new-meme.png" class="w-5 h-2.5 inline-block align-middle mb-0.5 ml-1" />'
                                            : ''
                                    }
                                </div>
                                <div style="color:#999;"> ${omitFunc(tokenAddress, 4, 4)}</div>
                            </div>

                        </div>
                        <div style="margin-top:8px">
                            <div>
                                <span>$${formatNumberToKMB(smartMoneyBuys, 2)} ${this.valueType === 'smart_buy' ? 'Smart' : 'Watchlist'} Buys</span>
                                <span style="color:#999;"> | </span>
                                <span>${formatPercent(percent, 'en-us', '1.0-2')}</span>
                            </div>

                            <div> $${formatNumberToKMB(marketCap, 2)} MKT Cap </div>

                            <div> $${formatNumberToKMB(liquidity, 2)} Liquidity </div>
                        </div>
                    </div>`;
        },
    });

    color: string;
    colorClass: string;

    loading = true;

    treemapOptions: EchartsOption = {
        tooltip: this.TREEMAP_CHART_TOOLTIP(),
    };
    treemapOptionsToBeUpdated: EchartsOption = {
        series: [
            {
                name: 'treemap',
                type: 'treemap',
                data: [],
                width: '100%',
                height: '100%',
                roam: false,
                nodeClick: false,
                breadcrumb: {
                    show: false,
                },

                itemStyle: {
                    borderRadius: 2,
                    gapWidth: 2,
                },
                label: {
                    position: [3, 3],
                    formatter: (params: any) => {
                        if (params.name === 'treemap') {
                            return '';
                        }

                        const value = formatNumberToKMB(params.data.realValue, 2);
                        const percent = formatPercent(params.data[this.percentKey], 'en-us', '1.0-2');

                        if (params.data.image) {
                            return `{${params.data.id}|}{name|${params.data.symbol}  }${
                                params.data.newMEME ? '  {new|}' : ''
                            }\n{value| $${value} } {line|${'|'}} {percent| ${percent} }`;
                        } else {
                            return `{${params.data.id}|${params.data.symbol?.slice(0, 2) || ''}}{name|${params.data.symbol}  }${
                                params.data.newMEME ? '  {new|}' : ''
                            }\n{value| $${value} } {line|${'|'}} {percent| ${percent} }`;
                        }
                    },
                    rich: {
                        name: {
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: 18,
                            color: '#000',
                            padding: [0, 0, 0, 4],
                        },
                        percent: {
                            fontSize: 12,
                            color: '#000',
                        },
                        value: {
                            fontSize: 12,
                            color: '#000',
                            lineHeight: 18,
                            padding: [0, 0, 0, 0],
                        },
                        line: {
                            color: 'rgba(0,0,0,0.2)',
                        },
                    },
                },
            },
        ],
    };

    get descText() {
        switch (this.type) {
            case 'small':
                return this.commonService.isMobile ? 'MC Small < $100K' : 'MKT Cap Small <$100K';
            case 'medium':
                return this.commonService.isMobile ? 'MC Mid $100K - $10M' : 'MKT Cap Mid $100K-$10M';
            case 'large':
                return this.commonService.isMobile ? 'MC Large > $10M' : 'MKT Cap Large >$10M';
        }
    }

    constructor(
        public memeDashboardService: MemeDashboardService,
        private cdr: ChangeDetectorRef,
        private commonService: CommonService,
        private router: Router
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data']?.currentValue) {
            this.generateTreemapChart();
            this.loading = false;
        }
    }

    ngOnInit() {
        switch (this.type) {
            case 'small':
                this.color = '#96EEDB';
                this.colorClass = cn('bg-[#96EEDB] bg-opacity-20 border-[#96EEDB] border hover:bg-opacity-40');
                break;
            case 'medium':
                this.color = '#CBF484';
                this.colorClass = cn('bg-[#CBF484] bg-opacity-20 border-[#CBF484] border hover:bg-opacity-40');
                break;
            case 'large':
                this.color = '#FEE384';
                this.colorClass = cn('bg-[#FEE384] bg-opacity-20 border-[#FEE384] border hover:bg-opacity-40');
                break;
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.echartsDirective.charts?.on('click', params => {
                if (params.componentSubType === 'treemap') {
                    // 这里可以获取到点击的节点数据
                    const tokenData = params.data as MemeToken;
                    this.router.navigate(['/', 'meme-dashboard', 'token', tokenData.chain_name, tokenData.token_address]);
                }
            });
        }, 100);
    }

    toggleDetailedTreemap() {
        this.shownDetailedTreemapChange.emit();

        // Wait for view to update then resize chart
        setTimeout(() => {
            this.echartsDirective.resizeChart();
            this.cdr.detectChanges();
        });
    }

    generateTreemapChart() {
        const convertedData = this.convertChainTreemapData(this.data);
        (this.treemapOptionsToBeUpdated.series as any)[0].data = convertedData;
        (this.treemapOptionsToBeUpdated.series as any)[0].levels = [
            {
                color: this.getColorsByMarketCapType(),
            },
        ];
        (this.treemapOptionsToBeUpdated.series as any)[0].label = {
            ...(this.treemapOptionsToBeUpdated.series as any)[0].label,
            rich: {
                ...(this.treemapOptionsToBeUpdated.series as any)[0].label?.rich,
                ...convertedData.reduce((acc, cur) => {
                    if (cur.image) {
                        acc[cur.id] = {
                            backgroundColor: {
                                image: cur.image,
                            },
                            width: 32,
                            height: 32,
                        };
                    } else {
                        acc[cur.id] = {
                            backgroundColor: '#F7F7F7',
                            padding: [0, 0, 0, 7],
                            height: 32,
                            width: 25,
                            fontFamily: 'Roboto Mono',
                            borderRadius: 16,
                            color: '#000',
                            fontSize: 14,
                            verticalAlign: 'middle',
                            lineHeight: 32,
                        };
                    }

                    acc[cur.chainWithID] = {
                        backgroundColor: {
                            image: getChainImg(cur.chain_name),
                        },
                        width: 12,
                        height: 12,
                        padding: [0, 0, 0, 0],
                        verticalAlign: 'bottom',
                    };

                    // 如果 token_created 在四小时内，那就添加new tag
                    if (cur.newMEME) {
                        acc.new = {
                            backgroundColor: {
                                image: '/assets/imgs/new-meme.png',
                            },
                            width: 24,
                            height: 20,
                            padding: [6, 0, 6, 0],
                        };
                    }

                    return acc;
                }, {} as any),
            },
        };
        this.treemapOptionsToBeUpdated = { ...this.treemapOptionsToBeUpdated };
    }

    convertChainTreemapData(data: MemeToken[]) {
        return convertTreemapData(
            data.map(item => ({
                name: item.name,
                value: item[this.valueKey],
            }))
        ).map((item, i) => {
            const colors = this.getColorsByMarketCapType();
            const originalItem = data[i];

            return {
                ...item,
                image: originalItem.logo,
                id: this.getIDByName(originalItem.name),
                chainWithID: `${this.getIDByName(originalItem.name)}_chain`,
                itemStyle: {
                    color: this.getBgColorByListType(colors, originalItem.list_type),
                    borderWidth: originalItem.list_type === 'both' ? 3 : 0,
                    borderColor: colors[3],
                },
                emphasis: {
                    // 自定义高亮样式
                    itemStyle: {
                        color: `${this.hexToRgba(this.getBgColorByListType(colors, originalItem.list_type), 0.9)}`, // 60% Opacity
                    },
                },
                processedImage: '',
                newMEME: originalItem.token_created_at ? dayjs().diff(dayjs(originalItem.token_created_at), 'hours') < 4 : false,
                ...originalItem,
            };
        });
    }

    private getIDByName(name: string) {
        return name?.replace(/[^a-zA-Z]/g, '_').toLowerCase();
    }

    private getColorsByMarketCapType() {
        switch (this.type) {
            case 'small':
                return smallColors;
            case 'medium':
                return midColors;
            case 'large':
                return largeColors;
        }
    }

    private getBgColorByListType(colors: string[], listType: MemeToken['list_type']) {
        switch (listType) {
            case 'smart_buy':
                return colors[0];
            case 'my_watchlist':
                return colors[1];
            case 'both':
                return colors[2];
        }
    }

    private hexToRgba(hex: string, opacity: number): string {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
}
